/**
 * Pure helpers for S3 multipart uploads plus the resume ledger.
 * The ledger (localStorage "cf.uploads") remembers, per file fingerprint, the multipart upload id, its part URLs
 * and the ETags of the parts already stored. Re-adding the same file after a reload or a dropped
 * connection continues from the first missing part instead of starting over.
 */
import type { SourceKind, UploadPlan } from '@/lib/api/types';

export interface ResumeRecord {
  sessionId: string;
  filename: string;
  size: number;
  kind: SourceKind;
  source_id: string;
  upload_id: string;
  part_size: number;
  part_urls: { n: number; url: string }[];
  /** part number → ETag */
  done: Record<number, string>;
  savedAt: number;
}

const KEY = 'cf.uploads';
const MAX_AGE_MS = 7 * 86400_000; // S3/MinIO abort incomplete multipart uploads after ~7 days

export function fingerprint(sessionId: string, file: { name: string; size: number; lastModified: number }): string {
  return `${sessionId}:${file.name}:${file.size}:${file.lastModified}`;
}

export function partCount(size: number, partSize: number): number {
  if (size <= 0) return 1;
  return Math.ceil(size / partSize);
}

/** Byte range [start, end) of 1-based part n. */
export function partRange(n: number, size: number, partSize: number): [number, number] {
  const start = (n - 1) * partSize;
  return [start, Math.min(size, start + partSize)];
}

export function pendingParts(count: number, done: Record<number, string>): number[] {
  const out: number[] = [];
  for (let n = 1; n <= count; n++) if (!done[n]) out.push(n);
  return out;
}

export function bytesDone(size: number, partSize: number, done: Record<number, string>): number {
  return Object.keys(done).reduce((sum, k) => {
    const [a, b] = partRange(Number(k), size, partSize);
    return sum + (b - a);
  }, 0);
}

/** Guess camera vs mic from the MIME type or extension. */
export function guessKind(file: { name: string; type: string }): SourceKind {
  if (file.type.startsWith('audio/')) return 'mic';
  if (file.type.startsWith('video/')) return 'camera';
  return /\.(wav|mp3|m4a|aac|flac|aif|aiff|ogg|opus)$/i.test(file.name) ? 'mic' : 'camera';
}

export function defaultLabel(filename: string): string {
  return filename.replace(/\.[^.]+$/, '');
}

export function isMediaFile(file: { name: string; type: string }): boolean {
  return file.type.startsWith('video/') || file.type.startsWith('audio/') || /\.(mp4|mov|m4v|mkv|mxf|avi|wav|mp3|m4a|aac|flac|aif|aiff)$/i.test(file.name);
}

// ── Ledger ──────────────────────────────────────────────────────
function readAll(): Record<string, ResumeRecord> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, ResumeRecord>;
    const now = Date.now();
    return Object.fromEntries(Object.entries(raw).filter(([, r]) => r && now - r.savedAt < MAX_AGE_MS));
  } catch {
    return {};
  }
}

function writeAll(all: Record<string, ResumeRecord>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch (e) {
    // Quota or private mode: resume after reload is lost, the upload itself still works.
    console.warn('[web:upload] could not save the resume ledger', e);
  }
}

export function loadRecord(fp: string): ResumeRecord | null {
  return readAll()[fp] ?? null;
}

export function saveRecord(fp: string, rec: ResumeRecord): void {
  const all = readAll();
  all[fp] = { ...rec, savedAt: Date.now() };
  writeAll(all);
}

export function clearRecord(fp: string): void {
  const all = readAll();
  delete all[fp];
  writeAll(all);
}

/** Unfinished uploads for a session (shown as "re-add to resume" after a reload). */
export function listRecords(sessionId: string): (ResumeRecord & { fp: string })[] {
  return Object.entries(readAll())
    .filter(([, r]) => r.sessionId === sessionId)
    .map(([fp, r]) => ({ ...r, fp }));
}

export function newRecord(sessionId: string, file: { name: string; size: number }, kind: SourceKind, plan: UploadPlan): ResumeRecord {
  return { sessionId, filename: file.name, size: file.size, kind, ...plan, done: {}, savedAt: Date.now() };
}
